import { useState, useEffect } from 'react';

// Utils
import api from '../../js/api';
import shopifyHelpers from '../../js/shopify';

const useCustomerAddress = () => {
    const [fetchingAddresses, setFetchingAddresses] = useState(false);
    const [updatingAddress, setUpdatingAddress] = useState(false);
    const [updateError, setUpdateError] = useState('');
    const [updateSuccess, setUpdateSuccess] = useState(false);
    const [addingAddress, setAddingAddress] = useState(false);
    const [addError, setAddError] = useState('');
    const [addSuccess, setAddSuccess] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const [addresses, setAddresses] = useState(null);
    const [countries, setCountries] = useState(null);
    const [provinces, setProvinces] = useState(null);

    const fetchCustomerAddresses = async (customerId) => {
        setFetchingAddresses(true);
        setAddresses(null);

        try {
            const accessToken = localStorage.getItem('accessToken');

            const CUSTOMER_QUERY = `
              query { customer(customerAccessToken: "${accessToken}") {
                id
                firstName
                lastName
                defaultAddress {
                    id
                    address1
                    address2
                    city
                    company
                    country
                    countryCodeV2
                    firstName
                    lastName
                    name
                    phone
                    province
                    provinceCode
                    zip
                }
                addresses(first: 10) {
                    edges {
                        node {
                            id
                            address1
                            address2
                            city
                            company
                            country
                            countryCodeV2
                            firstName
                            lastName
                            name
                            phone
                            province
                            provinceCode
                            zip
                        }
                    }
                }
                acceptsMarketing
                email
                phone
              }}`;
            const res = await fetch(`/api/2024-10/graphql.json`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Shopify-Storefront-Access-Token': api.getStorefrontToken()
                },
                body: JSON.stringify({
                    query: CUSTOMER_QUERY,
                    variables: {

                    },
                }),
            });

            if (!res.ok) {
                throw new Error('Failed to retrieve addresses. Please come back another time.');
            }


            const { data } = await res.json();

            // Extract and map through the edges to retrieve the address nodes
            const customerAddresses = data.customer?.addresses.edges.map(edge => edge.node);
            const defaultAddress = data.customer?.defaultAddress;

            customerAddresses?.forEach(address => {
                if (defaultAddress && address.id === defaultAddress.id) {
                    address.default = true;
                }
            });

            setAddresses(customerAddresses);
        } catch (error) {
            console.log(error);
        } finally {
            setFetchingAddresses(false);
        }
    };

    // Fetch provinces based on selected country
    const fetchProvincesFromCountry = async (countryCode) => {
        const res = await shopifyHelpers.getProvinces(countryCode);

        setProvinces(res);
    };

    const updateCustomerAddress = async (customerId: number, addressId: number, addressData: any) => {
        setUpdatingAddress(true);
        setUpdateError('');
        setUpdateSuccess(false);

        const accessToken = localStorage.getItem('accessToken');
        const isDefault = addressData.default;
        delete addressData.default;
        const addressPayload = {
            address: {
                ...addressData
            },
            customerAccessToken: accessToken,
            id: addressId,
        };

        if (isDefault) {
            await setAddressDefault(customerId, addressId);
        }

        const CUSTOMER_QUERY = `
            mutation customerAddressUpdate($address: MailingAddressInput!, $customerAccessToken: String!, $id: ID!) {
          customerAddressUpdate(address: $address, customerAccessToken: $customerAccessToken, id: $id) {
            customerAddress {
              id
            }
            customerUserErrors {
                code
                field
            }
            userErrors {
              field
              message
            }
          }
        }`;

        try {
            const res = await fetch(`/api/2024-10/graphql.json`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Shopify-Storefront-Access-Token': api.getStorefrontToken()
                },
                body: JSON.stringify({
                    query: CUSTOMER_QUERY,
                    variables: addressPayload,
                }),
            });

            if (!res.ok) {
                console.error('Failed to update address', res);

                throw new Error('Cannot update address at this time. Please try again later.');
            } else {
                setUpdateSuccess(true);
                fetchCustomerAddresses(customerId);

                return {
                    success: true
                };
            }
        } catch (error) {
            console.log(error);
            setUpdateError((error as Error).message);

            return {
                success: false
            };
        } finally {
            setUpdatingAddress(false);
        }
    };

    const addCustomerAddress = async (customerId: number, addressData: any) => {
        setAddingAddress(true);
        setAddError('');
        setAddSuccess(false);

        const isDefault = addressData.default;
        delete addressData.default;

        const accessToken = localStorage.getItem('accessToken');
        const addressPayload = {
            address: {
                ...addressData
            },
            customerAccessToken: accessToken
        };

        try {

            const CUSTOMER_QUERY = `mutation customerAddressCreate($address: MailingAddressInput!, $customerAccessToken: String!) {
              customerAddressCreate(address: $address, customerAccessToken: $customerAccessToken) {
                customerAddress {
                  id
                }
                customerUserErrors {
                  code
                  field
                }
                userErrors {
                  field
                  message
                }
              }
            }`;

            const res = await fetch(`/api/2024-10/graphql.json`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Shopify-Storefront-Access-Token': api.getStorefrontToken()
                },
                body: JSON.stringify({
                    query: CUSTOMER_QUERY,
                    variables: addressPayload,
                }),
            });


            if (isDefault){
                const { errors, data } = await res.json();
                setAddressDefault(customerId, data?.customerAddressCreate?.customerAddress?.id)
            }

            if (!res.ok) {
                console.error('Failed to add address', res);

                throw new Error('Cannot add address at this time. Please try again later.');
            } else {
                setAddSuccess(true);
                fetchCustomerAddresses(customerId);

                return {
                    success: true
                };
            }
        } catch (error) {
            console.log(error);
            setAddError((error as Error).message);

            return {
                success: false
            };
        } finally {
            setAddingAddress(false);
        }
    };

    const removeAddress = async (customerId: number, addressId: number) => {
        setDeleteError('');

        try {

            const accessToken = localStorage.getItem('accessToken');
            const CUSTOMER_QUERY = `mutation customerAddressDelete($customerAccessToken: String!, $id: ID!) {
              customerAddressDelete(customerAccessToken: $customerAccessToken, id: $id) {
                customerUserErrors {
                  code
                }
                deletedCustomerAddressId
                userErrors {
                  field
                  message
                }
              }
            }`;

            const res = await fetch(`/api/2024-10/graphql.json`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Shopify-Storefront-Access-Token': api.getStorefrontToken()
                },
                body: JSON.stringify({
                    query: CUSTOMER_QUERY,
                    variables: {
                        customerAccessToken: accessToken,
                        id: addressId
                    },
                }),
            });

            const { errors } = await res.json();

            if (!res.ok) {
                console.error('Failed to delete address', res);

                if (errors) {
                    throw new Error('Cannot delete the default address.');
                }

                throw new Error('Cannot delete address at this time. Please try again later.');
            } else {
                fetchCustomerAddresses(customerId);

                return {
                    success: true
                };
            }
        } catch (error) {
            console.log(error);
            setDeleteError((error as Error).message);

            setTimeout(() => {
                setDeleteError('');
            }, 5000);

            return {
                success: false
            };
        }
    };

    const setAddressDefault = async (customerId: number, addressId: number) => {
        setDeleteError('');

        try {

            const accessToken = localStorage.getItem('accessToken');
            const CUSTOMER_QUERY = `mutation customerDefaultAddressUpdate($addressId: ID!, $customerAccessToken: String!) {
              customerDefaultAddressUpdate(addressId: $addressId, customerAccessToken: $customerAccessToken) {
                customer {
                  id
                }
                customerUserErrors {
                  code
                  field
                }
                userErrors {
                  field
                  message
                }
              }
            }`;

            const res = await fetch(`/api/2024-10/graphql.json`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Shopify-Storefront-Access-Token': api.getStorefrontToken()
                },
                body: JSON.stringify({
                    query: CUSTOMER_QUERY,
                    variables: {
                        customerAccessToken: accessToken,
                        addressId: addressId
                    },
                }),
            });

            const { errors } = await res.json();

            if (!res.ok) {
                console.error('Failed to delete address', res);

                if (errors) {
                    throw new Error('Cannot delete the default address.');
                }

                throw new Error('Cannot delete address at this time. Please try again later.');
            } else {
                fetchCustomerAddresses(customerId);

                return {
                    success: true
                };
            }
        } catch (error) {
            console.log(error);
            setDeleteError((error as Error).message);

            setTimeout(() => {
                setDeleteError('');
            }, 5000);

            return {
                success: false
            };
        }
    };

    const resetStates = () => {
        setUpdateError('');
        setUpdateSuccess(false);
        setAddError('');
        setAddSuccess(false);
    };

    // Fetch available countries
    useEffect(() => {
        const fetchCountries = async () => {
            const res = await shopifyHelpers.getCountries();

            setCountries(res);
        };

        fetchCountries();
    }, []);

    return {
        fetchingAddresses,
        addresses,
        countries,
        provinces,
        updatingAddress,
        updateError,
        updateSuccess,
        addingAddress,
        addError,
        addSuccess,
        deleteError,

        fetchCustomerAddresses,
        fetchProvincesFromCountry,
        updateCustomerAddress,
        addCustomerAddress,
        resetStates,
        removeAddress
    };
};

export default useCustomerAddress;
